import React from "react";
import { InfoOutlined, PrintOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useTablePagination } from "hooks";
import { useLazyListVendorCodTransfersQuery } from "store/services/vendorProfileApi";
import { handleVendorPaidCodPrint } from "./HandleVendorPaidCodPrint";


const VendorCodTransferList = ({ vendorId, vendorName }) => {
  const [totalPaidAmount, setTotalPaidAmount] = React.useState(0);
  const [printLoading, setPrintLoading] = React.useState(false);

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [
    fetchCodTransfers,
    { data: codTransferRes, isLoading, isFetching },
  ] = useLazyListVendorCodTransfersQuery();

  // Fetch COD transfers (Paid COD)
  React.useEffect(() => {
    if (!vendorId) return;

    fetchCodTransfers({
      vendorId: vendorId,
      page: currentPage,
      limit: perPage,
    });
  }, [vendorId, currentPage, perPage, fetchCodTransfers]);

  // Calculate total paid amount
  React.useEffect(() => {
    if (codTransferRes?.data) {
      const total = codTransferRes.data.reduce(
        (sum, transfer) => sum + (transfer.transferAmount || 0),
        0
      );
      setTotalPaidAmount(total);
    }
  }, [codTransferRes]);

  const handlePrintClick = async () => {
    if (!vendorId) return;

    setPrintLoading(true);
    try {
      const res = await handleVendorPaidCodPrint(
        vendorId,
        fetchCodTransfers,
        vendorName
      );
      console.log("Print completed:", res);
    } catch (error) {
      console.error("Print error:", error);
    } finally {
      setPrintLoading(false);
    }
  };

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.codTransferId,
      cell: (row) => row?.codTransferId,
      width: "150px",
    },
    {
      name: "Transfer Date",
      selector: (row) => row?.transferDate,
      cell: (row) => <AppTableDateTime date={row?.transferDate} />,
    },
    {
      name: "Transfer Amount",
      selector: (row) => row?.transferAmount,
      cell: (row) => `Rs ${row?.transferAmount?.toFixed(2) || 0}`,
      right: true,
    },
    {
      name: "Added By",
      selector: (row) => row?.addedUser?.name || "N/A",
      cell: (row) => row?.addedUser?.name || "N/A",
    },
    {
      name: "Shipments",
      selector: (row) => row?.shipmentDetails?.join(", ") || "N/A",
      cell: (row) => row?.shipmentDetails?.join(", ") || "N/A",
    },
    {
      name: "Actions",
      cell: (row) => (
        <Link to={`/app/crm/cod-transfer/${row._id}/detail`}>
          <IconButton size="small" color="primary">
            <InfoOutlined />
          </IconButton>
        </Link>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  return (
    <BreadcrumbLayout.Paper>
      <Box display="flex" justifyContent="flex-end" m={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={
            printLoading ? <CircularProgress size={18} /> : <PrintOutlined />
          }
          onClick={handlePrintClick}
          disabled={printLoading || isLoading}
        >
          Paid COD Rs {totalPaidAmount.toFixed(2)}
        </Button>
      </Box>

      {codTransferRes ? (
        <AppTable
          columns={columns}
          data={codTransferRes?.data || []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={codTransferRes?.totalItems || 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      ) : (
        <Box display="flex" alignItems="center" justifyContent="center" p={2}>
          <CircularProgress size={24} />
          <Typography ml={1}>Loading COD Transfers…</Typography>
        </Box>
      )}
    </BreadcrumbLayout.Paper>
  );
};

export default VendorCodTransferList;